import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import signUp from "../../actions/SignUp";
import setPendingUserEmail from "../../actions/SetPendingUserEmail";
import LogoInverse from "../../../img/logo-inverse.svg";
import Logo from "../../../img/logo.svg";

import {
    ErrorText,
    FormGroup,
    Input,
    Label,
    Select,
} from "../../components/tailwind/form";

import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

const fieldClassName = (hasError) =>
    `tw-block tw-w-full tw-h-7 tw-font-nunito tw-bg-white/90 tw-border-0 tw-rounded-md tw-px-2 tw-py-0 tw-text-gray-800 tw-outline-none tw-ring-inset ${hasError ? "tw-ring-1 tw-ring-red-500" : "focus:tw-ring-2 focus:tw-ring-theme-blue"}`;

const features = [
    "Highly qualified leads matched to your targeting",
    "Stay in control with pay-per-lead pricing",
    "Integrated with leading comparison sites",
];

const SignUp = ({ markets, currencies, ...props }) => {
    const { signUpErrors } = props;

    const {
        register,
        handleSubmit,
        formState: { errors },
        setError,
        getValues,
    } = useForm();

    const [isSubmitting, setSubmitting] = useState(false);
    const history = useHistory();

    const onSubmit = (data) => {
        setSubmitting(true);

        props.signUp(data).then((success) => {
            if (!success) {
                setSubmitting(false);
                return;
            }

            props.setPendingUserEmail(data.email);
            history.push("/signup/success");
        });
    };

    useEffect(() => {
        if (signUpErrors) {
            Object.keys(signUpErrors).forEach((field) => {
                signUpErrors[field].forEach((message) => {
                    setError(field, {
                        type: "server",
                        message: message,
                    });
                });
            });
        }
    }, [signUpErrors, setError]);

    useEffect(() => {
        document.title = "Sign Up | Prospect Path";
    }, []);

    return (
        <div className="tw-min-h-screen tw-flex tw-relative tw-overflow-hidden tw-bg-gradient-to-br tw-from-theme-blue tw-to-theme-teal tw-font-nunito">
            <div className="tw-absolute tw-left-[-7rem] tw-bottom-[-3rem] tw-w-[30rem] tw-h-[30rem] tw-rounded-full tw-bg-theme-teal tw-blur-[80px] tw-pointer-events-none" aria-hidden="true" />

            <div className="tw-relative tw-hidden lg:tw-flex tw-flex-col tw-w-[40%] tw-pl-8 tw-pr-8 xl:tw-pl-16 tw-py-14">
                <a href="https://prospectpath.com/" target="_blank" rel="noreferrer" className="tw-ml-6">
                    <img src={LogoInverse} alt="Prospect Path" className="tw-h-[17px]" />
                </a>

                <div className="tw-mt-32 tw-max-w-[30rem]">
                    <span className="tw-inline-block tw-bg-white/30 tw-text-white tw-text-[11px] tw-font-semibold tw-uppercase tw-rounded-full tw-px-3 tw-py-1 tw-select-none">
                        Get started
                    </span>

                    <h1 className="tw-text-3xl tw-text-white tw-font-bold tw-leading-[1.35] tw-mt-4 tw-mb-6">
                        Find great customers for your software
                    </h1>

                    <p className="tw-text-white tw-text-lg tw-leading-relaxed tw-mb-14">
                        Tell us who you want to reach. We&apos;ll match you with leads other vendors couldn&apos;t use.
                    </p>

                    <ul className="tw-list-none tw-p-0 tw-m-0 tw-border-0 tw-border-t tw-border-solid tw-border-white/80">
                        {features.map((feature) => (
                            <li
                                key={feature}
                                className="tw-text-white tw-font-bold tw-text-sm xl:tw-text-base min-[1400px]:tw-text-lg tw-whitespace-nowrap tw-px-2 tw-py-5 tw-border-0 tw-border-b tw-border-solid tw-border-white/80"
                            >
                                {feature}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="tw-relative tw-flex-1 tw-flex tw-p-4 lg:tw-py-14 lg:tw-pr-16 lg:tw-pl-0">
                <div className="tw-w-full tw-bg-white/75 tw-rounded-lg tw-px-6 tw-py-6 lg:tw-px-10 lg:tw-py-7">
                    <div className="tw-flex tw-items-center tw-justify-between lg:tw-justify-end tw-gap-6">
                        <img src={Logo} alt="Prospect Path" className="tw-h-4 lg:tw-hidden" />

                        <div className="tw-flex tw-items-center tw-gap-6">
                            <span className="tw-hidden sm:tw-inline tw-text-sm tw-text-gray-700">
                                Already have an account?
                            </span>
                            <a
                                href="/login"
                                className="tw-bg-white/60 tw-text-gray-800 tw-text-base tw-font-semibold tw-rounded-lg tw-px-5 tw-py-2 tw-no-underline hover:tw-no-underline hover:tw-bg-white"
                            >
                                Log in
                            </a>
                        </div>
                    </div>

                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="tw-max-w-[36rem] tw-mx-auto tw-mt-8 lg:tw-mt-16"
                    >
                        <h2 className="tw-text-3xl tw-font-bold tw-text-gray-800 tw-mt-0 tw-mb-10">
                            Your details
                        </h2>

                        <h3 className="tw-text-sm tw-font-bold tw-uppercase tw-text-gray-800 tw-mt-0 tw-mb-4">
                            About you
                        </h3>

                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-20 tw-gap-y-4">
                            <FormGroup>
                                <Label className="tw-text-sm tw-text-gray-700 tw-mb-1">Full name</Label>
                                <Input
                                    name="name"
                                    className={fieldClassName(!!errors.name)}
                                    {...register("name", {
                                        required: "Full Name is required",
                                    })}
                                />
                                {errors.name && <ErrorText className="tw-font-nunito">{errors.name.message}</ErrorText>}
                            </FormGroup>

                            <FormGroup>
                                <Label className="tw-text-sm tw-text-gray-700 tw-mb-1">Company</Label>
                                <Input
                                    name="company_name"
                                    className={fieldClassName(!!errors.company_name)}
                                    {...register("company_name", {
                                        required: "Company name is required",
                                    })}
                                />
                                {errors.company_name && (
                                    <ErrorText className="tw-font-nunito">{errors.company_name.message}</ErrorText>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label className="tw-text-sm tw-text-gray-700 ">Email</Label>
                                <Input
                                    name="email"
                                    type="email"
                                    className={fieldClassName(!!errors.email)}
                                    {...register("email", {
                                        required: "Email is required",
                                    })}
                                />
                                {errors.email && (
                                    <ErrorText className="tw-font-nunito">{errors.email.message}</ErrorText>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label className="tw-text-sm tw-text-gray-700 ">Phone Number</Label>
                                <Input
                                    name="phone_number"
                                    className={fieldClassName(!!errors.phone_number)}
                                    {...register("phone_number", {
                                        required: "Phone number is required",
                                    })}
                                />
                                {errors.phone_number && (
                                    <ErrorText className="tw-font-nunito">{errors.phone_number.message}</ErrorText>
                                )}
                            </FormGroup>
                        </div>

                        <h3 className="tw-text-sm tw-font-bold tw-uppercase tw-text-gray-800 tw-mt-9 tw-mb-4">
                            Your targeting
                        </h3>

                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-20 tw-gap-y-5">
                            <FormGroup>
                                <Label className="tw-text-sm tw-text-gray-700 tw-mb-1">Currency</Label>
                                <Select
                                    name="currency"
                                    className={`${fieldClassName(!!errors.currency)} tw-pr-8`}
                                    {...register("currency", {
                                        required: "Currency is required",
                                    })}
                                >
                                    <option></option>
                                    {currencies.map((currency) => (
                                        <option key={currency.id} value={currency.id}>
                                            {currency.reference}
                                        </option>
                                    ))}
                                </Select>
                                {errors.currency && (
                                    <ErrorText className="tw-font-nunito">{errors.currency.message}</ErrorText>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label className="tw-text-sm tw-text-gray-700 tw-mb-1">Primary market</Label>
                                <Select
                                    name="primary_market"
                                    className={`${fieldClassName(!!errors.primary_market)} tw-pr-8`}
                                    {...register("primary_market", {
                                        required: "Primary market is required",
                                    })}
                                >
                                    <option></option>
                                    {markets.map((market) => (
                                        <option key={market.id} value={market.id}>
                                            {market.name}
                                        </option>
                                    ))}
                                </Select>
                                {errors.primary_market && (
                                    <ErrorText className="tw-font-nunito">{errors.primary_market.message}</ErrorText>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label className="tw-text-sm tw-text-gray-700 tw-mb-1">Employee Size Range Targeting</Label>
                                <div className="tw-flex tw-gap-4">
                                    <div className="tw-flex-1">
                                        <Input
                                            name="employee_size_min"
                                            type="number"
                                            placeholder="Min"
                                            className={fieldClassName(!!errors.employee_size_min)}
                                            {...register("employee_size_min", {
                                                required: "Minimum employee size is required",
                                                min: {
                                                    value: 1,
                                                    message: "Minimum must be at least 1",
                                                },
                                            })}
                                        />
                                        {errors.employee_size_min && (
                                            <ErrorText className="tw-font-nunito">{errors.employee_size_min.message}</ErrorText>
                                        )}
                                    </div>

                                    <div className="tw-flex-1">
                                        <Input
                                            name="employee_size_max"
                                            type="number"
                                            placeholder="Max"
                                            className={fieldClassName(!!errors.employee_size_max)}
                                            {...register("employee_size_max", {
                                                required: "Maximum employee size is required",
                                                validate: (value) => {
                                                    const minValue = getValues("employee_size_min");

                                                    if (!minValue || !value) return true;

                                                    return (
                                                        Number(value) >= Number(minValue) ||
                                                        "Max must be greater than or equal to Min"
                                                    );
                                                },
                                            })}
                                        />
                                        {errors.employee_size_max && (
                                            <ErrorText className="tw-font-nunito">{errors.employee_size_max.message}</ErrorText>
                                        )}
                                    </div>
                                </div>
                            </FormGroup>

                            <FormGroup>
                                <Label className="tw-text-sm tw-text-gray-700 tw-mb-1">Industry Targeting</Label>
                                <Input
                                    name="industry_targeting"
                                    className={fieldClassName(!!errors.industry_targeting)}
                                    {...register("industry_targeting", {
                                        required: "Industry targeting is required",
                                    })}
                                />
                                {errors.industry_targeting && (
                                    <ErrorText className="tw-font-nunito">{errors.industry_targeting.message}</ErrorText>
                                )}
                            </FormGroup>
                        </div>

                        <div className="tw-flex tw-justify-center tw-mt-12">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="tw-w-48 tw-h-10 tw-font-nunito tw-bg-white tw-text-theme-blue tw-text-base tw-font-bold tw-border-0 tw-rounded-lg tw-cursor-pointer hover:tw-bg-gray-100 disabled:tw-opacity-75"
                            >
                                Request access
                            </button>
                        </div>

                        <p className="tw-text-sm tw-text-center tw-text-gray-700 tw-mt-8 tw-mb-0">
                            By signing up, you agree to the{" "}
                            <a href="https://info.prospectpath.com/terms/" target="_blank" rel="noreferrer" className="tw-text-gray-800 tw-underline">
                                Terms of Service
                            </a>{" "}
                            and{" "}
                            <a href="https://info.prospectpath.com/privacy-policy/" target="_blank" rel="noreferrer" className="tw-text-gray-800 tw-underline">
                                Privacy Policy
                            </a>
                            .
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

SignUp.defaultProps = {
    markets: [],
    currencies: [],
};

SignUp.propTypes = {
    markets: PropTypes.array,
    currencies: PropTypes.array,
};

const mapStateToProps = (state) => ({
    signUpErrors: state.formErrors.userSignup,
});

const mapDispatchToProps = {
    signUp,
    setPendingUserEmail,
};

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
